import { useState } from "react";
import {
  Folder,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
  Lock,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/useMobileUtils";
import {
  useFolders,
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
} from "@/hooks/useQueries";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";

// Import TanStack Query hooks

const FOLDER_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#64748b",
];

// Color picker component
const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {FOLDER_COLORS.map((color) => (
      <button
        key={color}
        aria-label="Folder Colour"
        type="button"
        onClick={() => onChange(color)}
        className={cn(
          "h-8 w-8 rounded-full border-2 transition-transform hover:scale-110",
          value === color
            ? "border-foreground scale-110"
            : "border-transparent",
        )}
        style={{ backgroundColor: color }}
      />
    ))}
  </div>
);

// Folder card component for grid view
const FolderCard = ({ folder, onEdit, onDelete, onClick }) => (
  <Card
    className="group cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
    onClick={onClick}
  >
    <div
      className="h-32 flex items-center justify-center relative"
      style={{ backgroundColor: `${folder.color}15` }}
    >
      <div
        className="flex h-16 w-16 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${folder.color}25` }}
      >
        <Folder className="h-8 w-8" style={{ color: folder.color }} />
      </div>

      <div className="absolute top-2 right-2 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="secondary" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(folder);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(folder);
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <CardContent className="p-4">
      <h3 className="font-medium truncate mb-1">{folder.name}</h3>
      <p className="text-sm text-muted-foreground">
        {folder.envelopeCount || 0} envelope
        {(folder.envelopeCount || 0) !== 1 ? "s" : ""}
      </p>
    </CardContent>
  </Card>
);

// Folder list row component
const FolderListRow = ({ folder, onEdit, onDelete, onClick }) => (
  <TableRow className="cursor-pointer" onClick={onClick}>
    <TableCell>
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${folder.color}20` }}
        >
          <Folder className="h-5 w-5" style={{ color: folder.color }} />
        </div>
        <span className="font-medium">{folder.name}</span>
      </div>
    </TableCell>
    <TableCell className="text-muted-foreground">
      {folder.envelopeCount || 0} envelopes
    </TableCell>
    <TableCell>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit(folder);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete(folder);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  </TableRow>
);

// Loading skeleton
const FoldersSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-lg border bg-card overflow-hidden">
          <Skeleton className="h-32 rounded-none" />
          <div className="p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ onCreateClick }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Folder className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">{t("folders.noFolders")}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t("folders.noFoldersDesc")}
        </p>
        <Button onClick={onCreateClick}>
          <FolderPlus className="mr-2 h-4 w-4" />
          {t("folders.createFolder")}
        </Button>
      </CardContent>
    </Card>
  );
};

// Upgrade Required component
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("folders.title")}
        </h1>
        <p className="text-muted-foreground">{t("folders.subtitle")}</p>
      </div>

      {/* Upgrade Required Card */}
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.foldersUpgradeDesc",
              "Folders help you organize your documents and envelopes. Upgrade your plan to unlock this feature and keep your workspace tidy.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="mr-2 h-4 w-4" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Folder className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("upgrade.withFolders", "With Folders you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "upgrade.foldersFeature1",
                  "Organize envelopes into categories",
                )}
              </li>
              <li>
                {t(
                  "upgrade.foldersFeature2",
                  "Quickly find documents with folder filters",
                )}
              </li>
              <li>
                {t(
                  "upgrade.foldersFeature3",
                  "Color-code folders for easy identification",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const Folders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has folders feature - must be explicitly true
  // undefined or false means no access
  const hasFoldersFeature = user?.subscription?.features?.folders === true;

  // Local UI state only
  const [viewMode, setViewMode] = useState("grid");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [folderColor, setFolderColor] = useState(FOLDER_COLORS[0]);

  // ============================================
  // TanStack Query - Data fetching (only if feature enabled)
  // ============================================
  const { data: folders = [], isLoading } = useFolders({
    enabled: hasFoldersFeature,
  });

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  // ============================================
  // Feature Check - Show upgrade message if folders not available
  // ============================================
  if (!hasFoldersFeature) {
    return <UpgradeRequired />;
  }

  // ============================================
  // Event Handlers
  // ============================================
  const resetForm = () => {
    setFolderName("");
    setFolderColor(FOLDER_COLORS[0]);
    setSelectedFolder(null);
  };

  const handleCreateFolder = (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    createFolder.mutate(
      { name: folderName, color: folderColor },
      {
        onSuccess: () => {
          setShowCreateDialog(false);
          resetForm();
        },
      },
    );
  };

  const handleEditFolder = (e) => {
    e.preventDefault();
    if (!selectedFolder || !folderName.trim()) return;

    updateFolder.mutate(
      { id: selectedFolder.id, data: { name: folderName, color: folderColor } },
      {
        onSuccess: () => {
          setShowEditDialog(false);
          resetForm();
        },
      },
    );
  };

  const handleDeleteFolder = () => {
    if (!selectedFolder) return;

    deleteFolder.mutate(selectedFolder.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        resetForm();
      },
    });
  };

  const openEditDialog = (folder) => {
    setSelectedFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || FOLDER_COLORS[0]);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (folder) => {
    setSelectedFolder(folder);
    setShowDeleteDialog(true);
  };

  const handleFolderClick = (folder) => {
    navigate(`/envelopes?folder=${folder.id}`);
  };

  const renderContent = () => {
    if (folders.length === 0) {
      return <EmptyState onCreateClick={() => setShowCreateDialog(true)} />;
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {folders.map((folder) => (
          <FolderCard
            key={folder.id}
            folder={folder}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
            onClick={() => handleFolderClick(folder)}
          />
        ))}
      </div>
    ) : (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.envelopes")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {folders.map((folder) => (
              <FolderListRow
                key={folder.id}
                folder={folder}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
                onClick={() => handleFolderClick(folder)}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  // ============================================
  // Render
  // ============================================
  if (isLoading) {
    return <FoldersSkeleton />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("folders.title")}
          </h1>
          <p className="text-muted-foreground">{t("folders.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="hidden sm:inline-flex"
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            {t("folders.newFolder")}
          </Button>
        </div>
      </div>

      {/* Folders grid/list */}
      {renderContent()}

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton
          icon={FolderPlus}
          onClick={() => setShowCreateDialog(true)}
          label={t("folders.newFolder")}
        />
      )}

      {/* Create Folder Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("folders.createFolder")}</DialogTitle>
            <DialogDescription>
              {t("folders.createFolderDesc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFolder}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="folderName">{t("common.name")}</Label>
                <Input
                  id="folderName"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder={t("folders.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("folders.colour")}</Label>
                <ColorPicker value={folderColor} onChange={setFolderColor} />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                size={isMobile ? "mobile" : "default"}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createFolder.isPending || !folderName.trim()}
                size={isMobile ? "mobile" : "default"}
              >
                {createFolder.isPending
                  ? t("common.loading")
                  : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("folders.editFolder")}</DialogTitle>
            <DialogDescription>{t("folders.editFolderDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditFolder}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editFolderName">{t("common.name")}</Label>
                <Input
                  id="editFolderName"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder={t("folders.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("folders.color")}</Label>
                <ColorPicker value={folderColor} onChange={setFolderColor} />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                size={isMobile ? "mobile" : "default"}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={updateFolder.isPending || !folderName.trim()}
                size={isMobile ? "mobile" : "default"}
              >
                {updateFolder.isPending
                  ? t("common.loading")
                  : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("folders.deleteFolder")}</DialogTitle>
            <DialogDescription>
              {t("folders.deleteFolderDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">{selectedFolder?.name}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedFolder?.envelopeCount || 0} envelopes will be moved to
              "No Folder"
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFolder}
              disabled={deleteFolder.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {deleteFolder.isPending
                ? t("common.loading")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Folders;
